import { useEffect, useState } from 'react'

const INTERACTION_EVENTS = [
  'scroll',
  'click',
  'touchstart',
  'keydown',
  'mousemove'
] as const

/**
 * Returns `true` once the user interacts with the page OR the timeout elapses.
 * Use to defer non-critical third-party scripts past the initial render so
 * bouncers never download them.
 *
 * Default timeout is 20s — past Lighthouse's measurement window (~10-15s),
 * so deferred pixels don't inflate synthetic Perf scores. Real users virtually
 * always interact before 20s.
 */
export function useDelayedLoad(timeoutMs: number = 20000): boolean {
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    if (shouldLoad) return

    const trigger = () => setShouldLoad(true)
    const timer = window.setTimeout(trigger, timeoutMs)
    const options: AddEventListenerOptions = { once: true, passive: true }

    for (const event of INTERACTION_EVENTS) {
      window.addEventListener(event, trigger, options)
    }

    return () => {
      window.clearTimeout(timer)
      for (const event of INTERACTION_EVENTS) {
        window.removeEventListener(event, trigger)
      }
    }
  }, [shouldLoad, timeoutMs])

  return shouldLoad
}
